/**
 * Build the inputs for a combined elevation profile over several routes —
 * the same shape the planner's overview chart uses (see the planTrail
 * `createChartData` getter), so the hike map's profile reads identically.
 *
 * Two things the naive "flatten every coordinate" approach gets wrong:
 *  - Distance must RESTART at each route boundary. Routes can be
 *    disconnected (a drive between trailheads, a day that starts somewhere
 *    else), and measuring straight across the gap adds phantom kilometres to
 *    the axis and the header total.
 *  - Surfaces travel with the coordinates, index-for-index, or the profile
 *    falls back to the plain gold line instead of surface colouring.
 */
import { distance as turfDistance } from '@turf/turf';
import { expandSurfaceRanges, multiLineStringPointCount } from './expandSurfaceRanges';

export interface CombinedRouteSegment {
  /** MultiLineString coordinates: `[[[lon, lat, ele], …], …]`. */
  coordinates: number[][][];
  /** Range-compressed surfaces, `[[startIndex, endIndex, surfaceInt], …]`. */
  surfaceRanges?: Array<[number, number, number]> | null;
  /** Day the route belongs to, for day-boundary markers. */
  day?: number | null;
}

export interface DayMarker {
  /** Position on the x-axis, in the big unit (km / mi). */
  x: number;
  day: number;
  label: string;
}

export interface CombinedRouteProfileInput {
  coordinates: number[][];
  surfaces: (number | undefined)[];
  /** Cumulative distance in metres, parallel to `coordinates`. */
  distances: number[];
  dayMarkers: DayMarker[];
}

/**
 * How many days apart to space day-boundary markers. Short plans show every
 * day; long thru-hikes (PCT-scale, 100+ days) would otherwise overload the
 * chart with badges, so the interval widens as day count grows: every day up
 * to 20, every 2nd up to 50, every 3rd up to 100, then +1 per further 50 days.
 */
export function dayMarkerStep(totalDays: number): number {
  if (totalDays <= 20) return 1;
  if (totalDays <= 50) return 2;
  return 3 + Math.floor((totalDays - 51) / 50);
}

/**
 * Keep every Nth marker (see dayMarkerStep) plus the last, so the hike's end
 * is always visible. A single day needs no marker at all.
 */
export function thinDayMarkers(markers: DayMarker[]): DayMarker[] {
  if (markers.length < 2) return [];
  const step = dayMarkerStep(markers.length);
  if (step <= 1) return markers;
  return markers.filter((_, idx) => idx % step === 0 || idx === markers.length - 1);
}

export function buildCombinedRouteProfileInput(
  segments: CombinedRouteSegment[],
  toBigUnit: (km: number) => number
): CombinedRouteProfileInput {
  const coordinates: number[][] = [];
  const surfaces: (number | undefined)[] = [];
  const distances: number[] = [];
  const markers: DayMarker[] = [];
  const seenDays = new Set<number>();
  let cumulative = 0;

  segments.forEach((segment) => {
    const lines = Array.isArray(segment.coordinates) ? segment.coordinates : [];

    if (segment.day != null && !seenDays.has(segment.day)) {
      seenDays.add(segment.day);
      markers.push({ x: toBigUnit(cumulative / 1000), day: segment.day, label: String(segment.day) });
    }

    surfaces.push(...expandSurfaceRanges(segment.surfaceRanges, multiLineStringPointCount(lines)));

    // Restart at each route (no hop across the gap between routes), but
    // keep the lineStrings within one route joined up, as the planner does.
    let prev: number[] | null = null;
    lines.forEach((line) => {
      (Array.isArray(line) ? line : []).forEach((coord) => {
        if (prev) {
          cumulative += turfDistance(prev.slice(0, 2), coord.slice(0, 2), { units: 'meters' });
        }
        prev = coord;
        coordinates.push(coord);
        distances.push(cumulative);
      });
    });
  });

  return { coordinates, surfaces, distances, dayMarkers: thinDayMarkers(markers) };
}
